import {
  offset,
  useFloating,
  Middleware,
  Placement,
  Strategy,
  autoUpdate,
  safePolygon,
  useHover,
  useInteractions,
} from '@floating-ui/react';
import { ElementType, useMemo, useState } from 'react';
import { PolymorphProps } from './Polymorph';
import { Portal } from './Portal';
import clsx from 'clsx';

type TooltipProps<R extends ElementType> = {
  middleware?: Middleware[];
  placement?: Placement;
  strategy?: Strategy;
  floatingElement?: React.ReactNode;
  alwaysOpen?: boolean;
  floatingClassName?: string;
  disabled?: boolean;
  forceHidden?: boolean;
} & PolymorphProps<R>;

// Defined at module scope so the default reference is stable across renders.
// A fresh `[offset(8)]` array each render, combined with `whileElementsMounted:
// autoUpdate`, sends floating-ui into a re-render loop (ELE-850).
const DEFAULT_MIDDLEWARE: Middleware[] = [offset(8)];

export function Tooltip<E extends ElementType = 'div'>({
  middleware = DEFAULT_MIDDLEWARE,
  placement = 'top-end',
  strategy = 'fixed',
  as,
  floatingElement,
  alwaysOpen = false,
  floatingClassName,
  disabled = false,
  forceHidden = false,
  ...props
}: TooltipProps<E>) {
  const [open, setOpen] = useState(false);

  const {
    refs,
    floatingStyles,
    middlewareData,
    context: sendTooltipContext,
  } = useFloating({
    open: open,
    strategy: strategy,
    placement: placement,
    onOpenChange: setOpen,
    middleware: middleware,
    whileElementsMounted: autoUpdate,
  });
  // `safePolygon()` returns a fresh handler each call; created inline it gives useHover a new
  // `handleClose` reference every render, which — combined with `whileElementsMounted: autoUpdate`
  // — sends floating-ui into an infinite re-render loop (the freeze when the derived-income
  // tooltip wraps the disabled amount/frequency inputs). Memoize so the reference is stable.
  const handleClose = useMemo(() => safePolygon(), []);
  const hover = useHover(sendTooltipContext, { handleClose, enabled: !disabled });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  const Reference = as || 'div';

  return (
    <>
      <Reference {...props} ref={refs.setReference} {...getReferenceProps()} />
      <Portal>
        {(alwaysOpen || open) && !forceHidden && (
          <div
            ref={refs.setFloating}
            className={clsx('z-50', floatingClassName)}
            style={{
              ...floatingStyles,
              visibility: forceHidden || middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
            }}
            {...getFloatingProps()}
          >
            {floatingElement}
          </div>
        )}
      </Portal>
    </>
  );
}
